import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Truck, Search, Bookmark } from 'lucide-react';
import { Button } from './ui/button';
import ThemeToggle from './ThemeToggle';
import SearchAutocomplete from './SearchAutocomplete';
import { useBookmarks } from '../contexts/BookmarksContext';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();
  const { bookmarkCount } = useBookmarks();

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/accident-checklist', label: 'Checklist' },
    { path: '/legal-directory', label: 'Find Attorney' },
    { path: '/safety-statistics', label: 'Statistics' },
    { path: '/quiz', label: 'Safety Quiz' },
    { path: '/faq', label: 'FAQ' },
    { path: '/blog', label: 'Blog' },
    { path: '/resources', label: 'Resources' },
    { path: '/partners', label: 'Partners' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50 w-full border-b border-gray-700 bg-gray-900/95 backdrop-blur supports-[backdrop-filter]:bg-gray-900/80 dark:bg-gray-950/95">
      <div className="container mx-auto px-4">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 transition-opacity hover:opacity-80">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-orange-500 to-orange-600">
              <Truck className="h-6 w-6 text-white" />
            </div>
            <div className="flex flex-col">
              <span className="text-lg font-bold text-white leading-tight">TruckAccidentSupport</span>
              <span className="text-xs text-orange-400 leading-tight">Accident & Safety Resources</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-3 py-2 text-sm font-medium transition-colors rounded-md ${
                  isActive(link.path)
                    ? 'text-orange-400 bg-gray-800'
                    : 'text-gray-300 hover:text-orange-400 hover:bg-gray-800/50'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right Side Actions */}
          <div className="flex items-center gap-2">
            {/* Desktop Search */}
            <div className="hidden md:block relative">
              {searchOpen ? (
                <div className="w-64">
                  <SearchAutocomplete onClose={() => setSearchOpen(false)} />
                </div>
              ) : (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSearchOpen(true)}
                  className="text-gray-400 hover:text-orange-400"
                  data-testid="search-toggle-btn"
                >
                  <Search className="h-5 w-5" />
                </Button>
              )}
            </div>

            {/* Bookmarks */}
            <Link to="/bookmarks">
              <Button
                variant="ghost"
                size="icon"
                className="relative text-gray-400 hover:text-orange-400"
                data-testid="bookmarks-nav-btn"
              >
                <Bookmark className="h-5 w-5" />
                {bookmarkCount > 0 && (
                  <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-orange-500 text-[10px] font-bold text-white flex items-center justify-center">
                    {bookmarkCount > 9 ? '9+' : bookmarkCount}
                  </span>
                )}
              </Button>
            </Link>

            {/* Theme Toggle */}
            <ThemeToggle className="text-gray-400 hover:text-orange-400" />

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 text-gray-300 hover:text-orange-400 transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <nav className="lg:hidden py-4 border-t border-gray-700">
            {/* Mobile Search */}
            <div className="mb-4 px-2">
              <SearchAutocomplete onClose={() => setMobileMenuOpen(false)} />
            </div>
            
            <div className="flex flex-col space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-3 py-2 text-sm font-medium transition-colors rounded-md ${
                    isActive(link.path)
                      ? 'text-orange-400 bg-gray-800'
                      : 'text-gray-300 hover:text-orange-400 hover:bg-gray-800/50'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              
              {/* Mobile Bookmarks Link */}
              <Link
                to="/bookmarks"
                className={`px-3 py-2 text-sm font-medium transition-colors rounded-md flex items-center gap-2 ${
                  isActive('/bookmarks')
                    ? 'text-orange-400 bg-gray-800'
                    : 'text-gray-300 hover:text-orange-400 hover:bg-gray-800/50'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <Bookmark className="h-4 w-4" />
                Saved Articles
                {bookmarkCount > 0 && (
                  <span className="ml-auto bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full">
                    {bookmarkCount}
                  </span>
                )}
              </Link>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
